import React, { useState } from "react";

const FileExplorer = ({
  fileTree,
  onFileSelect,
  selectedFile,
  fileType = "PL/I",
  sidebarCollapsed = false,
  onToggleSidebar = () => {},
  // Analysis buttons props
  sessionId,
  isImpactAnalyzing,
  isDependencyAnalyzing,
  isUpdatingDocument,
  isAuthenticated,
  onRunAnalysis,
  onRunDependencyAnalysis,
  onUpdateDocument,
  showAnalysisButtons = false,
}) => {
  const [collapsedFolders, setCollapsedFolders] = useState(new Set());

  const toggleFolder = (folderPath) => {
    setCollapsedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  const renderFileTree = (tree, path = "") => {
    if (!tree || typeof tree !== "object") {
      return null;
    }

    return Object.entries(tree).map(([name, item]) => {
      const fullPath = path ? `${path}/${name}` : name;

      // Handle null items (files) and objects (folders)
      if (item === null || item === undefined) {
        // It's a file
        const isSelected = selectedFile === fullPath;
        return (
          <div
            key={fullPath}
            className={`file-item ${isSelected ? "selected" : ""}`}
            onClick={() => onFileSelect(fullPath)}
          >
            {/* <span className="file-icon">{getFileIcon(name)}</span> */}
            <span className="file-name">{name}</span>
          </div>
        );
      } else if (item && typeof item === "object") {
        // It's a folder - check if it has type property or is just an object with children
        const isFolder =
          item.type === "folder" ||
          (item.children && typeof item.children === "object");

        if (isFolder) {
          const isCollapsed = collapsedFolders.has(fullPath);
          const children = item.children || item; // Handle both structures

          return (
            <div key={fullPath} className="folder-item">
              <div
                className="folder-header"
                onClick={() => toggleFolder(fullPath)}
              >
                <span
                  className={`folder-toggle ${
                    isCollapsed ? "collapsed" : "expanded"
                  }`}
                >
                  {isCollapsed ? "▶" : "▼"}
                </span>
                <span className="folder-name">{name}</span>
                <button
                  className="folder-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    // You can add logic here to hide/remove the folder if needed
                    console.log("Close folder:", fullPath);
                  }}
                  title="Close folder"
                >
                  ×
                </button>
              </div>
              {!isCollapsed && (
                <div className="folder-children">
                  {renderFileTree(children, fullPath)}
                </div>
              )}
            </div>
          );
        } else {
          // It's a file but stored as an object
          const isSelected = selectedFile === fullPath;
          return (
            <div
              key={fullPath}
              className={`file-item ${isSelected ? "selected" : ""}`}
              onClick={() => onFileSelect(fullPath)}
            >
              {/* <span className="file-icon">{getFileIcon(name)}</span> */}
              <span className="file-name">{name}</span>
            </div>
          );
        }
      } else {
        // Fallback for any other data type
        const isSelected = selectedFile === fullPath;
        return (
          <div
            key={fullPath}
            className={`file-item ${isSelected ? "selected" : ""}`}
            onClick={() => onFileSelect(fullPath)}
          >
            {/* <span className="file-icon">{getFileIcon(name)}</span> */}
            <span className="file-name">{name}</span>
          </div>
        );
      }
    });
  };

  return (
    <div className="file-explorer">
      <div className="explorer-header">
        <h3
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexGrow: 1,
          }}
        >
          <img src="/files.svg" alt="Files" className="files-icon" />
          <span className="file-label">Files</span>
        </h3>
        <img
          src="/toggle.svg"
          alt={sidebarCollapsed ? "Open sidebar" : "Close sidebar"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSidebar();
          }}
          style={{
            width: "36px",
            height: "36px",
            transform: sidebarCollapsed ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.75s ease",
            cursor: "pointer",
          }}
        />
      </div>
      <div className="file-tree">
        {fileTree && Object.keys(fileTree).length > 0 ? (
          renderFileTree(fileTree)
        ) : (
          <div className="empty-explorer">
            <p>No {fileType.toLowerCase()} files loaded</p>
          </div>
        )}
      </div>

      {/* Analysis Buttons - Only shown for PL/I files */}
      {showAnalysisButtons && (
        <div className="analysis-buttons-container">
          <button
            className="analysis-button impact-analysis-button"
            onClick={onRunAnalysis}
            disabled={!sessionId || isImpactAnalyzing}
          >
            <img src="/run-icon.svg" alt="Run" className="button-icon" />
            {isImpactAnalyzing ? "Analyzing..." : "Run Impact Analysis"}
          </button>
          <button
            className="analysis-button dependency-analysis-button"
            onClick={onRunDependencyAnalysis}
            disabled={!sessionId || isDependencyAnalyzing || !isAuthenticated}
            title={
              !isAuthenticated
                ? "Please log in to run dependency analysis"
                : "Run dependency analysis"
            }
          >
            <img src="/graph-icon.svg" alt="Run" className="button-icon" />
            {isDependencyAnalyzing ? "Analyzing..." : "Run Dependency Analysis"}
            {!isAuthenticated && " 🔒"}
          </button>
          {onUpdateDocument && (
            <button
              className="analysis-button update-document-button"
              onClick={onUpdateDocument}
              disabled={!sessionId || isUpdatingDocument || !isAuthenticated}
              title={
                !isAuthenticated
                  ? "Please log in to update document"
                  : "Update document based on code changes"
              }
            >
              <img src="/run-icon.svg" alt="Update" className="button-icon" />
              {isUpdatingDocument ? "Updating..." : "Update Document"}
              {!isAuthenticated && " 🔒"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FileExplorer;
